import React, { useState } from 'react';
import {
  Settings,
  ChevronDown,
  ChevronRight,
  Shield,
  Upload,
  Check,
  AlertTriangle
} from 'lucide-react';
import ConfigChecklist from '../components/ConfigChecklist';
import { getConfigurationSteps } from '../../config/cisaMaturityModel';

/**
 * PillarConfigurationsTab - Configuration checklist grouped by level with evidence linking
 *
 * New features:
 * - Evidence indicators on completed steps
 * - Prompt to upload evidence when marking step complete
 * - Config status summary per product
 */
const PillarConfigurationsTab = ({
  pillarCode,
  pillar,
  functions,
  assessments,
  targetLevels,
  configCompletions,
  evidence = {},
  derivedScores = {},
  onToggleConfigStep,
  onAddEvidence
}) => {
  const [expandedFunctions, setExpandedFunctions] = useState(new Set());
  const [showEvidencePrompt, setShowEvidencePrompt] = useState(null);

  const LEVELS = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];

  const formatLevel = (level) => level.charAt(0) + level.slice(1).toLowerCase();

  // Toggle function expansion
  const toggleFunction = (functionCode) => {
    setExpandedFunctions(prev => {
      const next = new Set(prev);
      if (next.has(functionCode)) {
        next.delete(functionCode);
      } else {
        next.add(functionCode);
      }
      return next;
    });
  };

  // Handle config step toggle with evidence prompt
  const handleToggleStep = (functionCode, level, stepIndex, isCompleting) => {
    onToggleConfigStep?.(functionCode, level, stepIndex);

    // Show evidence prompt when completing a step
    if (isCompleting) {
      setShowEvidencePrompt({ functionCode, level, stepIndex });
    }
  };

  // Dismiss evidence prompt
  const dismissEvidencePrompt = () => {
    setShowEvidencePrompt(null);
  };

  // Handle adding evidence from prompt
  const handleAddEvidenceFromPrompt = () => {
    if (showEvidencePrompt) {
      onAddEvidence?.(showEvidencePrompt.functionCode);
      setShowEvidencePrompt(null);
    }
  };

  // Check if a function has evidence
  const hasEvidence = (functionCode) => {
    return (evidence[functionCode] || []).length > 0;
  };

  // Get evidence confidence for a function
  const getEvidenceConfidence = (functionCode) => {
    const derived = derivedScores[functionCode];
    return derived?.evidenceConfidence || 0;
  };

  // Build config data per function
  const functionsWithConfigs = [];

  for (const fn of functions) {
    const currentLevel = assessments[fn.code] || 'TRADITIONAL';
    const targetLevel = targetLevels[fn.code];
    const currentIndex = LEVELS.indexOf(currentLevel);
    const targetIndex = targetLevel ? LEVELS.indexOf(targetLevel) : currentIndex;

    // Get levels to configure (from current+1 to target)
    const levelsToConfig = [];
    for (let i = currentIndex + 1; i <= targetIndex && i < LEVELS.length; i++) {
      const level = LEVELS[i];
      const steps = getConfigurationSteps(fn.code, level);
      if (steps.length > 0) {
        const completion = configCompletions[`${fn.code}_${level}`] || { completedSteps: [], totalSteps: steps.length };
        levelsToConfig.push({
          level,
          steps,
          completedSteps: completion.completedSteps || [],
          totalSteps: steps.length
        });
      }
    }

    // Also show next level config if no target set
    if (!targetLevel && currentIndex < LEVELS.length - 1) {
      const nextLevel = LEVELS[currentIndex + 1];
      const steps = getConfigurationSteps(fn.code, nextLevel);
      if (steps.length > 0 && !levelsToConfig.some(l => l.level === nextLevel)) {
        const completion = configCompletions[`${fn.code}_${nextLevel}`] || { completedSteps: [], totalSteps: steps.length };
        levelsToConfig.push({
          level: nextLevel,
          steps,
          completedSteps: completion.completedSteps || [],
          totalSteps: steps.length
        });
      }
    }

    if (levelsToConfig.length > 0) {
      functionsWithConfigs.push({
        code: fn.code,
        name: fn.name,
        currentLevel,
        targetLevel,
        configs: levelsToConfig,
        hasEvidence: hasEvidence(fn.code),
        evidenceConfidence: getEvidenceConfidence(fn.code)
      });
    }
  }

  // Calculate overall completion stats
  let totalSteps = 0;
  let completedSteps = 0;

  for (const fn of functionsWithConfigs) {
    for (const config of fn.configs) {
      totalSteps += config.totalSteps;
      completedSteps += config.completedSteps.length;
    }
  }

  const overallCompletion = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  // Get confidence color
  const getConfidenceColor = (confidence) => {
    if (confidence >= 85) return '#10b981';
    if (confidence >= 65) return '#3b82f6';
    if (confidence >= 40) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className="cisa-pillar-configurations">
      {/* Header */}
      <div className="cisa-pillar-configurations-header">
        <Settings size={20} style={{ color: pillar.color }} />
        <div>
          <h3>Configuration Tracking</h3>
          <p>Step-by-step configuration for maturity progression</p>
        </div>
      </div>

      {/* Overall progress */}
      {totalSteps > 0 && (
        <div className="cisa-pillar-configurations-progress">
          <div className="cisa-pillar-configurations-progress-header">
            <span>Overall Configuration Progress</span>
            <span className="cisa-pillar-configurations-progress-percent">
              {completedSteps}/{totalSteps} ({overallCompletion}%)
            </span>
          </div>
          <div className="cisa-pillar-configurations-progress-bar">
            <div
              className="cisa-pillar-configurations-progress-fill"
              style={{ width: `${overallCompletion}%` }}
            />
          </div>
        </div>
      )}

      {/* Evidence prompt modal */}
      {showEvidencePrompt && (
        <div className="cisa-pillar-configurations-evidence-prompt">
          <div className="cisa-pillar-configurations-evidence-prompt-content">
            <Shield size={20} style={{ color: '#3b82f6' }} />
            <div className="cisa-pillar-configurations-evidence-prompt-text">
              <strong>Step Completed!</strong>
              <p>Would you like to upload evidence to verify this configuration?</p>
            </div>
            <div className="cisa-pillar-configurations-evidence-prompt-actions">
              <button
                type="button"
                className="cisa-pillar-configurations-evidence-prompt-btn cisa-pillar-configurations-evidence-prompt-btn--primary"
                onClick={handleAddEvidenceFromPrompt}
              >
                <Upload size={14} />
                Add Evidence
              </button>
              <button
                type="button"
                className="cisa-pillar-configurations-evidence-prompt-btn"
                onClick={dismissEvidencePrompt}
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Function configurations */}
      {functionsWithConfigs.length > 0 ? (
        <div className="cisa-pillar-configurations-list">
          {functionsWithConfigs.map(fn => {
            const isExpanded = expandedFunctions.has(fn.code);
            const fnTotalSteps = fn.configs.reduce((sum, c) => sum + c.totalSteps, 0);
            const fnCompletedSteps = fn.configs.reduce((sum, c) => sum + c.completedSteps.length, 0);
            const fnCompletion = fnTotalSteps > 0 ? Math.round((fnCompletedSteps / fnTotalSteps) * 100) : 0;

            return (
              <div key={fn.code} className="cisa-pillar-configurations-function">
                <button
                  type="button"
                  className="cisa-pillar-configurations-function-header"
                  onClick={() => toggleFunction(fn.code)}
                >
                  <div className="cisa-pillar-configurations-function-toggle">
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </div>
                  <div className="cisa-pillar-configurations-function-info">
                    <span className="cisa-pillar-configurations-function-name">{fn.name}</span>
                    <div className="cisa-pillar-configurations-function-meta">
                      <span className={`cisa-level-badge cisa-level--${fn.currentLevel.toLowerCase()}`}>
                        {formatLevel(fn.currentLevel)}
                      </span>
                      {fn.targetLevel && (
                        <span className="cisa-pillar-configurations-function-target">
                          → {formatLevel(fn.targetLevel)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Evidence indicator */}
                  <div className="cisa-pillar-configurations-function-evidence">
                    {fn.hasEvidence ? (
                      <div
                        className="cisa-pillar-configurations-function-evidence-badge"
                        style={{ color: getConfidenceColor(fn.evidenceConfidence) }}
                        title={`Evidence confidence: ${fn.evidenceConfidence}%`}
                      >
                        <Shield size={14} />
                        <span>{fn.evidenceConfidence}%</span>
                      </div>
                    ) : fnCompletedSteps > 0 ? (
                      <div
                        className="cisa-pillar-configurations-function-evidence-badge cisa-pillar-configurations-function-evidence-badge--missing"
                        title="No evidence uploaded"
                      >
                        <AlertTriangle size={14} />
                        <span>No evidence</span>
                      </div>
                    ) : null}
                  </div>

                  <div className="cisa-pillar-configurations-function-progress">
                    <div className="cisa-pillar-configurations-function-progress-bar">
                      <div
                        className="cisa-pillar-configurations-function-progress-fill"
                        style={{ width: `${fnCompletion}%` }}
                      />
                    </div>
                    <span className="cisa-pillar-configurations-function-progress-text">
                      {fnCompletion}%
                    </span>
                  </div>
                </button>

                {isExpanded && (
                  <div className="cisa-pillar-configurations-function-body">
                    {fn.configs.map(config => (
                      <ConfigChecklist
                        key={config.level}
                        functionCode={fn.code}
                        functionName={fn.name}
                        targetLevel={config.level}
                        completedSteps={config.completedSteps}
                        onToggleStep={(stepIndex) => {
                          const isCompleting = !config.completedSteps.includes(stepIndex);
                          handleToggleStep(fn.code, config.level, stepIndex, isCompleting);
                        }}
                        expanded={true}
                      />
                    ))}

                    {/* Evidence action for function */}
                    {fnCompletedSteps > 0 && !fn.hasEvidence && (
                      <div className="cisa-pillar-configurations-function-evidence-cta">
                        <Shield size={16} />
                        <span>Completed {fnCompletedSteps} step{fnCompletedSteps !== 1 ? 's' : ''}. Consider adding evidence to verify.</span>
                        <button
                          type="button"
                          className="cisa-pillar-configurations-function-evidence-cta-btn"
                          onClick={() => onAddEvidence?.(fn.code)}
                        >
                          <Upload size={14} />
                          Add Evidence
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="cisa-pillar-configurations-empty">
          <Settings size={32} />
          <h4>No Configurations to Track</h4>
          <p>
            Set target levels for functions to see configuration steps for progressing maturity.
          </p>
        </div>
      )}
    </div>
  );
};

export default PillarConfigurationsTab;
